import { useState } from 'react';

interface Course {
  id: number;
  tag: string;
  title: string;
  subtitle: string;
  description: string;
  duration: string;
  level: string;
  icon: string;
  highlights: string[];
  modules: { title: string; desc: string }[];
  price: string;
  originalPrice: string;
}

const courses: Course[] = [
  {
    id: 1,
    tag: '品牌、自媒體創作者',
    title: 'AI 短影音建構之旅',
    subtitle: '不僅只是做AI影片，而是要知道做什麼影片',
    description:
      '比起如何使用AI製作，更重要的是使用AI做什麼。從0到1的清晰路徑課程裏詳細拆解：帳號定位、人設打造、選題庫搭建、與AI協作腳本、AI影片製作流程',
    duration: '8 週',
    level: '零基礎入門',
    icon: 'ri-video-line',
    highlights: ['AI 影片生成工具', '提示詞優化', '短影音帳號定位', '人設搭建'],
    modules: [
      { title: 'AI 工具使用方式', desc: '認識並掌握主流 AI 影片生成工具的使用方法' },
      { title: '與AI協作腳本與分鏡', desc: '學習影片敘事結構，用 AI 快速生成專業腳本' },
      { title: '帳號定位', desc: '學會製作影片更要知道做什麼影片' },
      { title: '人設搭建', desc: '不要想著去成為誰，而是思考你是誰' },
    ],
    price: 'NT$ 2980',
    originalPrice: 'NT$ 3980',
  },
  {
    id: 2,
    tag: '廣告、穎是創作者',
    title: 'AI鏡頭大師之旅',
    subtitle: '不僅用AI做影片，還要用A做出好影片',
    description:
      '從零開始建立屬於你的短影音 IP 形象，學習如何運用 AI 工具規劃內容策略、生產高品質短影音，並在各大平台快速累積粉絲與流量。',
    duration: '12 週',
    level: '入門 → 進階',
    icon: 'ri-fire-line',
    highlights: ['AI 腳本自動生成', 'IP 設計', '建立自己的人設', 'AI短影音製作'],
    modules: [
      { title: '模組一：IP 定位與策略', desc: '找到你的差異化定位，建立獨特的個人品牌形象' },
      { title: '模組二：AI 內容生產流水線', desc: '用 AI 工具實現每週 20+ 支影片的批量生產' },
      { title: '模組三：爆款公式拆解', desc: '分析百萬播放影片的共同規律，複製成功模板' },
      { title: '模組四：流量變現策略', desc: '從粉絲到客戶，建立可持續的商業模式' },
    ],
    price: 'NT$ 7,880',
    originalPrice: 'NT$12000',
  },
  {
    id: 3,
    tag: '創業者、個人IP',
    title: '四相品牌沙龍',
    subtitle: '品牌與IP不只是一段故事，帶你從0開始用AI協作打造品牌',
    description:
      '這是為有一定基礎、想要突破天花板的創作者設計的進階課程。學習如何用 AI 指揮整個製作流程，從概念發想到最終成片，打造真正震撼人心的視覺作品。',
    duration: '16 週',
    level: '進階 → 大師',
    icon: 'ri-movie-2-line',
    highlights: ['電影級視覺特效', 'AI 角色動畫', '多鏡頭敘事', '商業案例實戰'],
    modules: [
      { title: '模組一：導演思維建立', desc: '從導演視角思考每一個鏡頭的視覺語言與情感傳遞' },
      { title: '模組二：AI 特效進階', desc: '掌握粒子系統、光效追蹤等高階視覺特效技術' },
      { title: '模組三：虛擬角色製作', desc: '創建並動畫化你的 AI 虛擬角色，打造獨特 IP' },
      { title: '模組四：商業案例實戰', desc: '完成一個完整的商業級影片專案，建立頂級作品集' },
    ],
    price: 'NT$ 12,800',
    originalPrice: 'NT$&nbsp; 48,800',
  },
];

const tagColors: Record<string, string> = {
  '廣告、穎是創作者': 'bg-amber-400/20 text-amber-300 border-amber-400/50',
  '品牌、自媒體創作者': 'bg-emerald-400/10 text-emerald-400 border-emerald-400/40',
  '創業者、個人IP': 'bg-rose-400/10 text-rose-400 border-rose-400/40',
};

export default function Courses() {
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const toggle = (id: number) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  return (
    <section id="courses" className="relative py-32 px-8 bg-black overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-black via-amber-950/10 to-black pointer-events-none" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[1px] bg-gradient-to-r from-transparent via-amber-500/30 to-transparent" />

      <div className="relative z-10 max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-20">
          <div className="flex items-center justify-center gap-4 mb-6">
            <div className="w-12 h-px bg-gradient-to-r from-transparent to-amber-400" />
            <span
              className="text-amber-400/80 text-xs tracking-[0.4em] font-light uppercase"
              style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
            >
              COURSES · ENROLLMENT
            </span>
            <div className="w-12 h-px bg-gradient-to-l from-transparent to-amber-400" />
          </div>
          <h2
            className="text-5xl md:text-6xl font-black leading-tight tracking-tight mb-6"
            style={{ fontFamily: "'Noto Serif TC', serif" }}
          >
            <span
              style={{
                background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 30%, #fde68a 60%, #d97706 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                filter: 'drop-shadow(0 0 30px rgba(251,191,36,0.4))',
              }}
            >
              選擇你的課程
            </span>
          </h2>
          <p
            className="text-white/75 text-base leading-relaxed max-w-xl mx-auto"
            style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
          >
            無論你是完全零基礎，還是想突破創作天花板<br />
            我們都有適合你的課程，帶你站上 AI 時代的浪頭
          </p>
        </div>

        {/* Course Cards */}
        <div className="flex flex-col gap-6">
          {courses.map((course, index) => {
            const isOpen = expandedId === course.id;
            return (
              <div
                key={course.id}
                className={`relative rounded-sm border transition-all duration-500 overflow-hidden ${
                  isOpen
                    ? 'border-amber-400/60'
                    : 'border-amber-500/20 hover:border-amber-400/40'
                }`}
                style={{ background: isOpen ? 'rgba(251,191,36,0.04)' : 'rgba(255,255,255,0.02)' }}
              >
                {/* Card Header — always visible */}
                <button
                  onClick={() => toggle(course.id)}
                  className="w-full text-left px-8 py-8 flex flex-col md:flex-row md:items-center gap-6 cursor-pointer group"
                >
                  {/* Index + Icon */}
                  <div className="flex items-center gap-5 flex-shrink-0">
                    <span
                      className="text-amber-400/30 text-4xl font-black leading-none"
                      style={{ fontFamily: "'Noto Serif TC', serif" }}
                    >
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <div
                      className="w-14 h-14 flex items-center justify-center rounded-sm flex-shrink-0"
                      style={{ background: 'linear-gradient(135deg, #d97706, #fbbf24)' }}
                    >
                      <i className={`${course.icon} text-2xl text-black`} />
                    </div>
                  </div>

                  {/* Title block */}
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-3 mb-2">
                      <span
                        className={`inline-block px-3 py-0.5 text-[10px] tracking-[0.3em] border rounded-sm font-medium ${tagColors[course.tag]}`}
                        style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
                      >
                        {course.tag}
                      </span>

                    </div>
                    <h3
                      className="text-2xl md:text-3xl font-bold text-white mb-1 group-hover:text-amber-300 transition-colors duration-300"
                      style={{ fontFamily: "'Noto Serif TC', serif" }}
                    >
                      {course.title}
                    </h3>
                    <p
                      className="text-white/75 text-sm leading-relaxed"
                      style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
                    >
                      {course.subtitle}
                    </p>
                  </div>

                  {/* Price + Toggle */}
                  <div className="flex items-center gap-6 flex-shrink-0">
                    <div className="text-right">
                      <div
                        className="text-white/50 text-xs line-through mb-0.5"
                        style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
                      >
                        {course.originalPrice}
                      </div>
                      <div
                        className="text-2xl font-black"
                        style={{
                          fontFamily: "'Noto Serif TC', serif",
                          background: 'linear-gradient(135deg, #fbbf24, #f59e0b)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                        }}
                      >
                        {course.price}
                      </div>
                    </div>
                    <div
                      className={`w-10 h-10 flex items-center justify-center rounded-full border transition-all duration-300 flex-shrink-0 ${
                        isOpen
                          ? 'bg-amber-400/20 border-amber-400/60 rotate-180'
                          : 'bg-white/5 border-white/30 group-hover:border-amber-400/40'
                      }`}
                    >
                      <i className="ri-arrow-down-s-line text-xl text-amber-400" />
                    </div>
                  </div>
                </button>

                {/* Expanded Detail */}
                <div
                  className={`transition-all duration-500 overflow-hidden ${
                    isOpen ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-8 pb-10 border-t border-amber-500/20">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 pt-8">
                      {/* Left: Description + Highlights */}
                      <div>
                        <p
                          className="text-white/80 text-sm leading-relaxed mb-8"
                          style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
                        >
                          {course.description}
                        </p>
                        <div className="mb-6">
                          <h4
                            className="text-amber-400/80 text-xs tracking-[0.3em] uppercase mb-4"
                            style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
                          >
                            課程亮點
                          </h4>
                          <div className="grid grid-cols-2 gap-3">
                            {course.highlights.map((h) => (
                              <div key={h} className="flex items-center gap-2">
                                <i className="ri-checkbox-circle-fill text-amber-400 text-sm flex-shrink-0" />
                                <span
                                  className="text-white/85 text-sm"
                                  style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
                                >
                                  {h}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {/* Right: Modules */}
                      <div>
                        <h4
                          className="text-amber-400/80 text-xs tracking-[0.3em] uppercase mb-4"
                          style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
                        >
                          課程模組
                        </h4>
                        <div className="flex flex-col gap-3">
                          {course.modules.map((mod, i) => (
                            <div
                              key={mod.title}
                              className="flex gap-4 p-4 rounded-sm border border-amber-500/10 bg-amber-500/5"
                            >
                              <span
                                className="text-amber-400/60 text-xs font-bold mt-0.5 flex-shrink-0"
                                style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
                              >
                                {String(i + 1).padStart(2, '0')}
                              </span>
                              <div>
                                <div
                                  className="text-white text-sm font-semibold mb-1"
                                  style={{ fontFamily: "'Noto Serif TC', serif" }}
                                >
                                  {mod.title}
                                </div>
                                <div
                                  className="text-white/65 text-xs leading-relaxed"
                                  style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
                                >
                                  {mod.desc}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* CTA */}
                    <div className="mt-8 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                      <a
                        href="#courses"
                        onClick={(e) => {
                          e.preventDefault();
                          document.getElementById('courses')?.scrollIntoView({ behavior: 'smooth' });
                        }}
                        className="inline-flex items-center gap-2 px-8 py-3 rounded-sm font-bold text-black text-sm tracking-widest whitespace-nowrap transition-all duration-300 hover:scale-105"
                        style={{
                          background: 'linear-gradient(135deg, #d97706, #fbbf24)',
                          fontFamily: "'Noto Sans TC', sans-serif",
                        }}
                      >
                        <i className="ri-send-plane-fill text-base" />
                        立即報名諮詢
                      </a>
                      <span
                        className="text-white/55 text-xs"
                        style={{ fontFamily: "'Noto Sans TC', sans-serif" }}
                      >
                        名額有限，報名後將有專人與您聯繫確認
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
